import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { connect } from 'react-redux';

import { getPlatformDetailsSelector } from '../../platform/platformSelector';
import platformSingleton from '../../platform/platformSingleton';
import { isPlatformIframe, isPlatformPhoneOrMobileMode } from '../../platform/utils/platformDetailsUtils';
import { setGridSize } from './gridActions';
import { setGridSizeLocalStorage } from './gridSizeStorageService';
import { calculateGridSize, getGridSizeBodyClassName, getStoredGridSize, isMobileGridName } from './gridUtils';

import { prop } from '../../../common/utils/immutableHelper';

import { GRID_SIZE_LIST } from './gridConstants';

const mapStateToProps = (state) => ({
    gridSize: state.getIn(['app', 'gridSize']),
    platformDetails: getPlatformDetailsSelector(state),
});

const mapDispatchToProps = (dispatch) => ({
    updateGridSize: (size, name) => dispatch(setGridSize(size, name)),
});

const getSizeName = prop('name');
const getSizeInitialized = prop('initialized');

const ALL_GRID_SIZE_CLASS_NAMES = GRID_SIZE_LIST.map((gridSize) => getGridSizeBodyClassName(gridSize.name));

const GridSizeManager = ({ gridSize, updateGridSize, platformDetails }) => {
    const removeBodyClass = () => {
        document.body.classList.remove(...ALL_GRID_SIZE_CLASS_NAMES);
        document.documentElement.classList.remove(...ALL_GRID_SIZE_CLASS_NAMES);
    };

    const setBodyClass = (sizeName) => {
        const className = getGridSizeBodyClassName(sizeName);

        removeBodyClass();
        document.body.classList.add(className);
        document.documentElement.classList.add(className);
    };

    // on grid size change
    useEffect(() => {
        if (!getSizeInitialized(gridSize)) return;

        const sizeName = getSizeName(gridSize);

        if (sizeName) {
            setBodyClass(sizeName);
            !isPlatformIframe(platformDetails) && setGridSizeLocalStorage(sizeName);
        }

        return removeBodyClass;
    }, [gridSize]);

    // on mount, initialise grid size
    useEffect(() => {
        const storedGrid = getStoredGridSize();

        // If this is mobile and its gotten into a non-mobile  state, we want to
        // re-calculate the grid size
        // Conversely, if this is not mobile and it's gotten into a mobile state, we want
        // to re-calculate the grid size as well
        const isMobilePlatform = isPlatformPhoneOrMobileMode(platformSingleton);
        const isGridMobile = isMobileGridName(storedGrid?.name);
        if (isMobilePlatform !== isGridMobile) {
            const { size, name } = calculateGridSize(screen.width, screen.height, platformSingleton.features.isTouch);
            updateGridSize(size, name);
            return;
        }

        if (getSizeInitialized(gridSize)) return;

        // Otherwise use the stored grid size or re-calculate if it's undefined
        const { size, name } =
            storedGrid || calculateGridSize(screen.width, screen.height, platformSingleton.features.isTouch);
        updateGridSize(size, name);
    }, []);

    return null;
};

GridSizeManager.propTypes = {
    gridSize: PropTypes.object,
    updateGridSize: PropTypes.func,
    platformDetails: PropTypes.object,
};

export default connect(mapStateToProps, mapDispatchToProps)(GridSizeManager);
